/*
 * Amen Supply - global behaviour (loaded deferred on every page)
 * - Overlay: one controller for drawers and popups (focus trap, Esc, scrim, focus return, scroll lock)
 * - <announcement-bar>, <header-menu>, <email-popup>
 * - Email form validation for [data-email-form]
 * - Quick add: single-variant products add straight away; products with options open the #QuickAdd picker
 * Section-specific behaviour lives in its own file (hero.js, cart.js, product.js ...).
 */
(() => {
  'use strict';

  const designMode = () => Boolean(window.Shopify && window.Shopify.designMode);
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const session = {
    get(key) { try { return sessionStorage.getItem(key); } catch (e) { return null; } },
    set(key, value) { try { sessionStorage.setItem(key, value); } catch (e) { /* storage is optional */ } },
  };

  /* ---------- Analytics hooks ---------- */
  // Custom events for Shopify Customer events (Settings > Customer events), e.g. a GA4 or Klaviyo pixel can
  // subscribe with analytics.subscribe('amen_email_signup', ...). Standard shop events are tracked by Shopify.
  const track = (name, data = {}) => {
    try { window.Shopify?.analytics?.publish?.(name, data); } catch (e) { /* analytics is optional */ }
  };
  window.AmenTrack = track;

  /* ---------- Focus helpers ---------- */
  const FOCUSABLE = [
    'a[href]', 'area[href]', 'button:not([disabled])', 'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])', 'textarea:not([disabled])', 'iframe', '[tabindex]:not([tabindex="-1"])',
  ].join(',');
  const focusableIn = (root) => [...root.querySelectorAll(FOCUSABLE)].filter((el) => el.getClientRects().length > 0);

  function trapTab(event, root) {
    const items = focusableIn(root);
    if (!items.length) { event.preventDefault(); root.focus(); return; }
    const first = items[0], last = items[items.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === root)) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }

  /* ---------- Overlay controller ---------- */
  // Markup contract: <div class="scrim" data-scrim="ID" data-close></div><div id="ID" data-overlay aria-hidden="true" tabindex="-1">
  const Overlay = {
    current: null,
    opener: null,

    open(el, opener) {
      if (!el || el === this.current) return;
      if (this.current) this.close({ returnFocus: false });
      this.current = el;
      this.opener = opener || document.activeElement;
      el.classList.add('is-on');
      el.setAttribute('aria-hidden', 'false');
      this.scrimFor(el)?.classList.add('is-on');
      this.syncTriggers(el, true);
      document.body.classList.add('has-overlay');
      document.addEventListener('keydown', this.onKeydown);
      // Wait for visibility to flip before moving focus.
      window.setTimeout(() => {
        if (this.current !== el) return;
        const target = el.querySelector('[data-autofocus]') || focusableIn(el)[0] || el;
        target.focus({ preventScroll: true });
      }, 60);
      document.dispatchEvent(new CustomEvent('overlay:open', { detail: { el } }));
      if (el.id === 'EmailPopup') track('amen_email_popup_opened', { trigger: opener && opener !== document.body ? 'click' : 'auto' });
    },

    close({ returnFocus = true } = {}) {
      const el = this.current;
      if (!el) return;
      el.classList.remove('is-on');
      el.setAttribute('aria-hidden', 'true');
      this.scrimFor(el)?.classList.remove('is-on');
      this.syncTriggers(el, false);
      document.body.classList.remove('has-overlay');
      document.removeEventListener('keydown', this.onKeydown);
      this.current = null;
      if (returnFocus && this.opener && document.contains(this.opener)) this.opener.focus({ preventScroll: true });
      this.opener = null;
      document.dispatchEvent(new CustomEvent('overlay:close', { detail: { el } }));
    },

    scrimFor(el) { return el.id ? document.querySelector(`[data-scrim="${el.id}"]`) : null; },

    syncTriggers(el, expanded) {
      if (!el.id) return;
      document.querySelectorAll(`[aria-controls="${el.id}"]`).forEach((b) => b.setAttribute('aria-expanded', String(expanded)));
    },

    onKeydown: (event) => {
      if (!Overlay.current) return;
      if (event.key === 'Escape') { event.preventDefault(); Overlay.close(); }
      else if (event.key === 'Tab') trapTab(event, Overlay.current);
    },
  };
  window.AmenOverlay = Overlay;

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-open]');
    if (opener) {
      const el = document.getElementById(opener.dataset.open);
      if (el) { event.preventDefault(); Overlay.open(el, opener); }
      return;
    }
    const popupTrigger = event.target.closest('[data-open-popup]');
    if (popupTrigger) {
      const popup = document.getElementById('EmailPopup');
      if (popup) { event.preventDefault(); Overlay.open(popup, popupTrigger); }
      return;
    }
    if (!Overlay.current) return;
    if (event.target.closest('[data-close]')) { Overlay.close(); return; }
    // Drawer links close the drawer (same-page anchors like #mission need it; navigation does not care).
    const link = event.target.closest('a[href]');
    if (link && Overlay.current.contains(link) && Overlay.current.hasAttribute('data-close-on-link')) Overlay.close({ returnFocus: false });
  });

  document.addEventListener('shopify:section:unload', () => Overlay.close({ returnFocus: false }));

  /* ---------- Email form validation ---------- */
  // Server-side Shopify handles the subscription; this only gives the wireframe's inline message.
  document.addEventListener('submit', (event) => {
    const form = event.target.closest('form[data-email-form]');
    if (!form) return;
    const input = form.querySelector('input[type="email"]');
    const msg = document.getElementById(form.dataset.msgId);
    if (input && (!input.value.trim() || !input.validity.valid)) {
      event.preventDefault();
      input.setAttribute('aria-invalid', 'true');
      if (msg) msg.textContent = form.dataset.errorEmail || '';
      input.focus();
      return;
    }
    track('amen_email_signup', { form: form.id });
  });
  // Contact form: required email and message, with the wireframe's inline messages.
  // The markup keeps the browser's own `required` check for anyone without JS; with JS we show ours instead.
  document.querySelectorAll('form[data-contact-form]').forEach((form) => { form.noValidate = true; });
  document.addEventListener('submit', (event) => {
    const form = event.target.closest('form[data-contact-form]');
    if (!form) return;
    const msg = form.querySelector('[role="status"]');
    const email = form.querySelector('input[type="email"]');
    const body = form.querySelector('textarea');
    let problem = null;
    if (email && (!email.value.trim() || !email.validity.valid)) problem = [email, msg?.dataset.msgEmail];
    else if (body && !body.value.trim()) problem = [body, msg?.dataset.msgBody];
    if (!problem) { track('amen_contact_submit', { topic: form.querySelector('select')?.value || '' }); return; }
    event.preventDefault();
    problem[0].setAttribute('aria-invalid', 'true');
    if (msg) msg.textContent = problem[1] || '';
    problem[0].focus();
  });

  document.addEventListener('input', (event) => {
    if (event.target.matches('form[data-contact-form] [aria-invalid="true"]')) event.target.removeAttribute('aria-invalid');
    if (event.target.matches('form[data-email-form] input[aria-invalid="true"]')) event.target.removeAttribute('aria-invalid');
  });

  // After a post Shopify reloads with #<form id>. Every customer form on the page sees the result,
  // so keep it only on the form that was submitted.
  const postedFormId = window.location.hash.slice(1);
  const emailFormPosted = (form) => Boolean(form.querySelector('[data-form-result]')) && (!postedFormId || postedFormId === form.id);
  document.querySelectorAll('form[data-email-form]').forEach((form) => {
    if (form.querySelector('[data-form-result]') && !emailFormPosted(form)) {
      const msg = document.getElementById(form.dataset.msgId);
      if (msg) msg.textContent = '';
      form.querySelector('[aria-invalid]')?.removeAttribute('aria-invalid');
    }
  });

  /* ---------- Policy pages: put the meta line and summary right under Shopify's title ---------- */
  const policyIntro = document.querySelector('[data-policy-intro]');
  const policyTitle = document.querySelector('.shopify-policy__title');
  if (policyIntro && policyTitle) policyTitle.after(policyIntro);

  /* ---------- Copy link (Journal share row) ---------- */
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-copy-link]');
    if (!button) return;
    try { await navigator.clipboard.writeText(button.dataset.copyLink); } catch (e) { /* clipboard is optional */ }
    const status = button.parentElement.querySelector('[role="status"]');
    if (status) status.textContent = button.dataset.copiedLabel || '';
  });

  /* ---------- Cart count + quick add ---------- */
  const routes = window.routes || {};

  async function refreshCartCount() {
    const res = await fetch(`${routes.cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } });
    const cart = await res.json();
    document.querySelectorAll('[data-cart-count]').forEach((el) => { el.textContent = cart.item_count; el.hidden = cart.item_count === 0; });
    document.querySelectorAll('[data-cart-open]').forEach((link) => {
      const label = cart.item_count === 1 ? link.dataset.labelOne : link.dataset.labelOther;
      if (label) link.setAttribute('aria-label', label.replace('[count]', cart.item_count));
    });
    return cart;
  }
  window.AmenCart = { refreshCount: refreshCartCount };

  async function addVariant(variantId, trigger) {
    const res = await fetch(`${routes.cart_add_url || '/cart/add'}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
    });
    if (!res.ok) throw new Error(`Add to cart failed (${res.status})`);
    const cart = await refreshCartCount();
    track('amen_quick_add', { variant_id: Number(variantId), url: trigger.dataset.productUrl });
    // The cart drawer handles this event and cancels it; without a drawer, go to the cart page.
    const added = new CustomEvent('cart:added', { detail: { cart, trigger }, cancelable: true });
    if (document.dispatchEvent(added)) window.location.href = routes.cart_url || '/cart';
  }

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-quick-add]');
    if (!button) return;
    event.preventDefault();
    if (button.getAttribute('aria-busy') === 'true') return;
    button.setAttribute('aria-busy', 'true');
    try {
      // Products with options (size, color) open the picker; nothing is added until the shopper chooses.
      if (button.hasAttribute('data-choose')) await QuickAdd.open(button);
      else await addVariant(button.dataset.variantId, button);
    } catch (error) {
      // Sold out, a variant problem or no picker: let the product page handle the choice.
      window.location.href = button.dataset.productUrl;
    } finally {
      button.removeAttribute('aria-busy');
    }
  });

  /* ---------- Quick add picker (sections/quick-add.liquid inside the #QuickAdd dialog) ---------- */
  const QuickAdd = {
    async open(button) {
      const dialog = document.getElementById('QuickAdd');
      const slot = dialog && dialog.querySelector('[data-quick-add-slot]');
      if (!slot) throw new Error('No quick add dialog');
      const url = new URL(button.dataset.productUrl, window.location.origin);
      url.searchParams.set('section_id', 'quick-add');
      const res = await fetch(url, { headers: { Accept: 'text/html' } });
      if (!res.ok) throw new Error(`Quick add failed (${res.status})`);
      const body = new DOMParser().parseFromString(await res.text(), 'text/html').querySelector('[data-quick-add-body]');
      if (!body) throw new Error('Quick add markup missing');
      slot.replaceChildren(body);
      this.bind(body, button);
      Overlay.open(dialog, button);
    },

    bind(body, trigger) {
      const form = body.querySelector('form');
      const variants = JSON.parse(body.querySelector('[data-qa-json]').textContent).variants;
      const groups = [...form.querySelectorAll('[data-option-group]')];
      const fixed = [...form.querySelectorAll('input[type="hidden"][data-option]')];
      const msg = form.querySelector('[data-qa-msg]');
      const price = body.querySelector('[data-qa-price]');
      const addButton = form.querySelector('[data-qa-add]');
      const addLabel = addButton.textContent;

      const chosen = () => {
        const options = [];
        fixed.forEach((input) => { options[Number(input.dataset.option) - 1] = input.value; });
        groups.forEach((group) => {
          const input = group.querySelector('input:checked');
          options[Number(group.dataset.optionGroup) - 1] = input ? input.value : null;
        });
        return options;
      };
      const find = (options) => variants.find((v) => v.options.every((value, i) => value === options[i]));

      const refresh = () => {
        const options = chosen();
        // Strike through choices with no available variant for what is already chosen (same as the product page).
        groups.forEach((group) => {
          const pos = Number(group.dataset.optionGroup) - 1;
          group.querySelectorAll('input[data-option]').forEach((input) => {
            const ok = variants.some((v) => v.available && v.options[pos] === input.value
              && v.options.every((value, i) => i === pos || options[i] == null || value === options[i]));
            if (input.nextElementSibling) input.nextElementSibling.classList.toggle('is-unavailable', !ok);
          });
        });
        const colorName = form.querySelector('[data-color-name]');
        const colorInput = colorName && colorName.closest('[data-option-group]').querySelector('input:checked');
        if (colorInput) colorName.textContent = colorInput.value;
        const variant = options.every((value) => value != null) ? find(options) : null;
        if (variant) price.textContent = variant.price;
        addButton.textContent = variant && !variant.available ? msg.dataset.msgSoldOut : addLabel;
      };

      form.addEventListener('change', () => { msg.textContent = ''; refresh(); });
      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        const missing = groups.find((group) => !group.querySelector('input:checked'));
        if (missing) {
          msg.textContent = msg.dataset.msgChoose;
          const first = missing.querySelector('input');
          if (first) first.focus();
          return;
        }
        const variant = find(chosen());
        if (!variant || !variant.available) { msg.textContent = msg.dataset.msgSoldOut; return; }
        addButton.setAttribute('aria-busy', 'true');
        try {
          Overlay.close({ returnFocus: false });
          await addVariant(variant.id, trigger);
        } catch (error) {
          window.location.href = trigger.dataset.productUrl;
        } finally {
          addButton.removeAttribute('aria-busy');
        }
      });
      refresh();
    },
  };

  /* ---------- <announcement-bar> ---------- */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.messages = [...this.querySelectorAll('.announce__msg')];
      this.index = Math.max(0, this.messages.findIndex((m) => m.classList.contains('is-on')));
      this.paused = false;
      if (this.messages.length < 2) return;
      const pause = () => { this.paused = true; };
      const resume = () => { if (!this.editing) this.paused = false; };
      this.addEventListener('mouseenter', pause);
      this.addEventListener('mouseleave', resume);
      this.addEventListener('focusin', pause);
      this.addEventListener('focusout', resume);
      this.addEventListener('shopify:block:select', (e) => { this.editing = true; this.paused = true; this.show(this.messages.indexOf(e.target)); });
      this.addEventListener('shopify:block:deselect', () => { this.editing = false; this.paused = false; });
      if (reducedMotion()) return;
      const interval = Number(this.dataset.interval) || 5000;
      this.timer = window.setInterval(() => { if (!this.paused && !document.hidden) this.show(this.index + 1); }, interval);
    }

    disconnectedCallback() { window.clearInterval(this.timer); }

    show(i) {
      const n = this.messages.length, next = (i + n) % n;
      if (next === this.index || next < 0) return;
      const cur = this.messages[this.index], nxt = this.messages[next];
      cur.classList.remove('is-on');
      cur.classList.add('is-out');
      // Park the incoming message below without animating, then slide it up.
      nxt.style.transition = 'none';
      nxt.classList.remove('is-out');
      void nxt.offsetWidth;
      nxt.style.transition = '';
      nxt.classList.add('is-on');
      this.index = next;
    }
  }
  customElements.define('announcement-bar', AnnouncementBar);

  /* ---------- <header-menu>: Shop All dropdown ---------- */
  class HeaderMenu extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button[aria-controls]');
      this.panel = this.button && document.getElementById(this.button.getAttribute('aria-controls'));
      if (!this.panel) return;
      this.onButton = (e) => { e.stopPropagation(); this.toggle(!this.isOpen); };
      this.onDocClick = (e) => { if (this.isOpen && !this.contains(e.target)) this.toggle(false); };
      this.onKeydown = (e) => { if (e.key === 'Escape' && this.isOpen) { this.toggle(false); this.button.focus(); } };
      this.onFocusOut = (e) => { if (this.isOpen && !this.contains(e.relatedTarget)) this.toggle(false); };
      this.button.addEventListener('click', this.onButton);
      document.addEventListener('click', this.onDocClick);
      document.addEventListener('keydown', this.onKeydown);
      this.addEventListener('focusout', this.onFocusOut);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocClick);
      document.removeEventListener('keydown', this.onKeydown);
    }

    get isOpen() { return this.button.getAttribute('aria-expanded') === 'true'; }

    toggle(open) {
      this.button.setAttribute('aria-expanded', String(open));
      this.panel.classList.toggle('is-open', open);
    }
  }
  customElements.define('header-menu', HeaderMenu);

  /* ---------- <email-popup> ---------- */
  // Opens once per session after a delay or at a scroll depth, whichever comes first.
  // Also opens from any [data-open-popup] trigger, and re-opens after a submit to show the result.
  class EmailPopup extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[data-overlay]');
      if (!this.dialog) return;
      this.addEventListener('shopify:section:select', () => Overlay.open(this.dialog));
      this.addEventListener('shopify:section:deselect', () => { if (Overlay.current === this.dialog) Overlay.close({ returnFocus: false }); });

      const form = this.querySelector('form[data-email-form]');
      if (form && emailFormPosted(form)) {
        session.set('amen-popup-seen', '1');
        Overlay.open(this.dialog);
        return;
      }
      if (designMode() || !this.hasAttribute('data-auto') || session.get('amen-popup-seen')) return;

      const delay = Number(this.dataset.delay || 12) * 1000;
      const depth = Number(this.dataset.scrollDepth || 50) / 100;
      this.autoOpen = () => {
        if (this.fired) return;
        if (Overlay.current) return; // never interrupt another drawer; try again on the next scroll
        this.fired = true;
        this.stop();
        session.set('amen-popup-seen', '1');
        Overlay.open(this.dialog, document.body);
      };
      this.onScroll = () => {
        const max = document.documentElement.scrollHeight - window.innerHeight;
        if (max > 0 && window.scrollY / max >= depth) this.autoOpen();
      };
      this.timer = window.setTimeout(this.autoOpen, delay);
      window.addEventListener('scroll', this.onScroll, { passive: true });
    }

    disconnectedCallback() { this.stop(); }

    stop() {
      window.clearTimeout(this.timer);
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
    }
  }
  customElements.define('email-popup', EmailPopup);
})();
